import React, { useCallback, useEffect, useState } from "react";
import {
    Autocomplete,
    Box,
    CircularProgress,
    IconButton,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    autocompleteClasses,
} from "@mui/material";
import Popper from "@mui/material/Popper";
import { styled } from "@mui/material/styles";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import moment from "moment";
import { publicApi } from "../../api/http";
import PlayerComparisonGraph from "../../components/PlayerComparisonGraph/PlayerComparisonGraph";
import "./PlayerCompareTab.css";

const ComparePopper = styled(Popper)({
    [`& .${autocompleteClasses.listbox}`]: {
        boxSizing: "border-box",
        backgroundColor: "#1a1f25",
        color: "#f3f4f6",
        border: "1px solid rgba(255, 255, 255, 0.09)",
        borderRadius: "10px",
        padding: "4px",
        boxShadow: "0 12px 32px rgba(0, 0, 0, 0.45)",
        "& li": {
            borderRadius: "6px",
            padding: "8px 12px",
            fontSize: "13px",
        },
        "& li:hover": {
            backgroundColor: "rgba(126, 168, 98, 0.12)",
        },
    },
});

const compareFieldSx = {
    "& .MuiOutlinedInput-root": {
        color: "var(--text-primary)",
        borderRadius: 2,
        fontSize: "0.85rem",
        "& fieldset": { borderColor: "#2d3344" },
        "&:hover fieldset": { borderColor: "#4a5568" },
        "&.Mui-focused fieldset": { borderColor: "var(--brand-strong)" },
    },
    "& .MuiInputLabel-root": { color: "var(--text-muted)", fontSize: "0.85rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: "var(--brand-strong)" },
};

const viewToggleSx = {
    flexShrink: 0,
    "& .MuiToggleButtonGroup-grouped": {
        margin: 0,
    },
    "& .MuiToggleButton-root": {
        color: "var(--text-muted)",
        borderColor: "var(--border-default)",
        fontSize: "0.72rem",
        fontWeight: 600,
        textTransform: "none",
        padding: "2px 7px",
        gap: "3px",
        minHeight: 28,
        lineHeight: 1.2,
        "& .MuiSvgIcon-root": {
            fontSize: "0.9rem",
        },
        "&.Mui-selected": {
            color: "var(--text-primary)",
            backgroundColor: "rgba(126, 168, 98, 0.18)",
            borderColor: "var(--brand-strong)",
            "&:hover": {
                backgroundColor: "rgba(126, 168, 98, 0.24)",
            },
        },
    },
};

function sortPlayerOptions(list) {
    return [...list].sort((a, b) => {
        const aName = `${a.last_name} ${a.first_name}`.toLowerCase();
        const bName = `${b.last_name} ${b.first_name}`.toLowerCase();
        return aName.localeCompare(bName);
    });
}

function buildGraphData(rows) {
    if (!rows?.length) return null;
    return rows.map((item) => ({
        effective_date: moment(item.effective_date).valueOf(),
        score: Math.round(item.score * 1e2) / 1e2,
        lastRank: item.has_data_point ? item.last_rank : null,
        consensusRanking: item.rank,
        lastRankType: item.last_rank_type,
    }));
}

function formatStatValue(value, { includeDecimal, isPercent } = {}) {
    if (value === undefined || value === null || Number.isNaN(value)) {
        return "—";
    }
    if (isPercent) {
        return `${(Number(value) * 100).toFixed(1)}%`;
    }
    if (includeDecimal) {
        return Number(value).toFixed(2);
    }
    return String(value);
}

function statWinnerClass(left, right, higherBetter) {
    if (left == null || right == null || left === right) {
        if (left != null && right != null && left === right) return "compare-stat-value--tie";
        return "";
    }
    const leftWins = higherBetter ? left > right : left < right;
    if (leftWins) return "compare-stat-value--winner";
    return "compare-stat-value--loser";
}

function CompareStatRow({ label, leftRaw, rightRaw, higherBetter = true, includeDecimal, isPercent }) {
    const leftNum = leftRaw != null && leftRaw !== "" ? Number(leftRaw) : null;
    const rightNum = rightRaw != null && rightRaw !== "" ? Number(rightRaw) : null;
    const opts = { includeDecimal, isPercent };
    const leftWinner = statWinnerClass(leftNum, rightNum, higherBetter);
    const rightWinner = statWinnerClass(rightNum, leftNum, higherBetter);

    return (
        <div className="compare-stat-row">
            <div
                className={`compare-stat-value compare-stat-value--primary ${leftWinner}`}
                title="This player"
            >
                {formatStatValue(leftRaw, opts)}
            </div>
            <div className="compare-stat-label">{label}</div>
            <div
                className={`compare-stat-value compare-stat-value--compare ${rightWinner}`}
                title="Compared player"
            >
                {formatStatValue(rightRaw, opts)}
            </div>
        </div>
    );
}

function CompareStatsPanel({ playerData, comparePlayerData, primaryStats, compareStats, primaryConsensus, compareConsensus }) {
    const sections = [
        {
            title: "Season stats",
            rows: [
                { label: "Games played", left: primaryStats?.games_played, right: compareStats?.games_played, higherBetter: true },
                { label: "Points / game", left: primaryStats?.points_per_game, right: compareStats?.points_per_game, higherBetter: true, includeDecimal: true },
                { label: "Goals / game", left: primaryStats?.goals_per_game, right: compareStats?.goals_per_game, higherBetter: true, includeDecimal: true },
                { label: "Assists / game", left: primaryStats?.assists_per_game, right: compareStats?.assists_per_game, higherBetter: true, includeDecimal: true },
                { label: "PIM / game", left: primaryStats?.penalty_minutes_per_game, right: compareStats?.penalty_minutes_per_game, higherBetter: false, includeDecimal: true },
            ],
        },
        {
            title: "Physical",
            rows: [
                { label: "Weight (lbs)", left: playerData?.weight, right: comparePlayerData?.weight, higherBetter: true },
                { label: "Height", left: playerData?.height, right: comparePlayerData?.height, higherBetter: true },
            ],
        },
        {
            title: "Consensus rankings",
            rows: [
                { label: "Best rank", left: primaryConsensus?.best_rank, right: compareConsensus?.best_rank, higherBetter: false },
                { label: "Worst rank", left: primaryConsensus?.worst_rank, right: compareConsensus?.worst_rank, higherBetter: false },
                { label: "Ranking count", left: primaryConsensus?.count, right: compareConsensus?.count, higherBetter: true },
            ],
        },
    ];

    const primaryName = playerData ? `${playerData.first_name} ${playerData.last_name}` : "Player 1";
    const compareName = comparePlayerData
        ? `${comparePlayerData.first_name} ${comparePlayerData.last_name}`
        : "Player 2";

    return (
        <div className="compare-stats-panel">
            <div className="compare-stats-header">
                <div className="compare-stats-col compare-stats-col--primary">
                    <span className="compare-stats-swatch compare-stats-swatch--primary" aria-hidden="true" />
                    <div className="compare-stats-col-text">
                        <span className="compare-player-name">{primaryName}</span>
                        <span className="compare-stats-role">This player · left column</span>
                    </div>
                </div>
                <span className="compare-stats-vs" aria-hidden="true">
                    vs
                </span>
                <div className="compare-stats-col compare-stats-col--compare">
                    <span className="compare-stats-swatch compare-stats-swatch--compare" aria-hidden="true" />
                    <div className="compare-stats-col-text">
                        <span className="compare-player-name">{compareName}</span>
                        <span className="compare-stats-role">Compared to · right column</span>
                    </div>
                </div>
            </div>
            <div className="compare-stats-column-labels" aria-hidden="true">
                <span className="compare-stats-column-label compare-stats-column-label--primary">
                    {primaryName}
                </span>
                <span className="compare-stats-column-label compare-stats-column-label--metric">Stat</span>
                <span className="compare-stats-column-label compare-stats-column-label--compare">
                    {compareName}
                </span>
            </div>
            {sections.map((section) => (
                <div key={section.title} className="compare-stats-section">
                    <div className="compare-stats-section-title">{section.title}</div>
                    {section.rows.map((row) => (
                        <CompareStatRow
                            key={row.label}
                            label={row.label}
                            leftRaw={row.left}
                            rightRaw={row.right}
                            higherBetter={row.higherBetter}
                            includeDecimal={row.includeDecimal}
                            isPercent={row.isPercent}
                        />
                    ))}
                </div>
            ))}
        </div>
    );
}

function PlayerCompareTab({ playerId, playerData, isActive }) {
    const [comparePlayer, setComparePlayer] = useState(null);
    const [compareOptions, setCompareOptions] = useState([]);
    const [allCompareOptions, setAllCompareOptions] = useState([]);
    const [searchInput, setSearchInput] = useState("");
    const [viewMode, setViewMode] = useState("stats");

    const [primaryStats, setPrimaryStats] = useState(null);
    const [primaryGraphData, setPrimaryGraphData] = useState(null);
    const [primaryConsensus, setPrimaryConsensus] = useState(null);

    const [compareStats, setCompareStats] = useState(null);
    const [compareGraphData, setCompareGraphData] = useState(null);
    const [compareConsensus, setCompareConsensus] = useState(null);
    const [comparePlayerData, setComparePlayerData] = useState(null);

    const [loadingPrimary, setLoadingPrimary] = useState(false);
    const [loadingCompare, setLoadingCompare] = useState(false);

    const clearCompare = useCallback(() => {
        setComparePlayer(null);
        setComparePlayerData(null);
        setCompareStats(null);
        setCompareGraphData(null);
        setCompareConsensus(null);
        setSearchInput("");
        setCompareOptions([]);
        setAllCompareOptions([]);
        setViewMode("stats");
    }, []);

    useEffect(() => {
        if (!isActive) return;
        clearCompare();
    }, [playerId, isActive, clearCompare]);

    useEffect(() => {
        if (!isActive || !playerId) return;

        let cancelled = false;
        setLoadingPrimary(true);

        const statsParams = playerData?.draft_year ? { draft_year: playerData.draft_year } : {};

        Promise.all([
            publicApi.get(`/player_stats_per_game/${playerId}`, { params: statsParams }),
            publicApi.get(`/consensus_rankings/${playerId}`),
        ])
            .then(([statsRes, consensusRes]) => {
                if (cancelled) return;
                setPrimaryStats(statsRes.data);
                const rows = consensusRes.data || [];
                setPrimaryConsensus(rows[rows.length - 1] || null);
                setPrimaryGraphData(buildGraphData(rows));
            })
            .catch((err) => {
                if (!cancelled) {
                    console.warn("[compare] primary load failed", err);
                    setPrimaryStats(null);
                    setPrimaryConsensus(null);
                    setPrimaryGraphData(null);
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingPrimary(false);
            });

        return () => {
            cancelled = true;
        };
    }, [isActive, playerId, playerData?.draft_year]);

    useEffect(() => {
        if (!comparePlayer?.player_id) {
            setComparePlayerData(null);
            setCompareStats(null);
            setCompareGraphData(null);
            setCompareConsensus(null);
            return;
        }

        let cancelled = false;
        setLoadingCompare(true);
        const compareId = comparePlayer.player_id;

        Promise.all([
            publicApi.get(`/players/${compareId}`),
            publicApi.get(`/consensus_rankings/${compareId}`),
        ])
            .then(([playerRes, consensusRes]) => {
                if (cancelled) return;
                const compareData = playerRes.data;
                setComparePlayerData(compareData);
                const compareStatsParams = compareData?.draft_year
                    ? { draft_year: compareData.draft_year }
                    : {};
                return publicApi
                    .get(`/player_stats_per_game/${compareId}`, { params: compareStatsParams })
                    .then((statsRes) => {
                        if (cancelled) return;
                        setCompareStats(statsRes.data);
                        return { compareData, consensusRes };
                    });
            })
            .then((result) => {
                if (cancelled || !result) return;
                const { consensusRes } = result;
                const rows = consensusRes.data || [];
                setCompareConsensus(rows[rows.length - 1] || null);
                setCompareGraphData(buildGraphData(rows));
            })
            .catch((err) => {
                if (!cancelled) {
                    console.warn("[compare] compare load failed", err);
                    clearCompare();
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingCompare(false);
            });

        return () => {
            cancelled = true;
        };
    }, [comparePlayer?.player_id, clearCompare]);

    const fetchCompareOptions = async (value) => {
        try {
            const params = playerData?.draft_year ? { draft_year: playerData.draft_year } : {};
            const response = await publicApi.get(`/players/search/${value}`, { params });
            const filtered = (response.data || []).filter((p) => p.player_id !== playerId);
            const sorted = sortPlayerOptions(filtered);
            setAllCompareOptions(sorted);
            setCompareOptions(sorted);
        } catch (err) {
            console.error("Error fetching compare options:", err);
        }
    };

    const handleSearchInputChange = (value) => {
        setSearchInput(value);
        if (value.length === 3) {
            fetchCompareOptions(value);
        } else if (value.length > 3) {
            const constructedPlayerId = value.replace(/\s/g, "_").toLowerCase();
            const filtered = allCompareOptions.filter((option) =>
                option.player_id.includes(constructedPlayerId),
            );
            setCompareOptions(sortPlayerOptions(filtered));
        } else {
            setCompareOptions([]);
        }
    };

    const primaryName = playerData
        ? `${playerData.first_name} ${playerData.last_name}`
        : "This player";

    const hasGraphData =
        (primaryGraphData && primaryGraphData.length > 0) ||
        (compareGraphData && compareGraphData.length > 0);

    return (
        <Box className="player-compare-tab" hidden={!isActive}>
            <Box className="player-compare-toolbar">
                <Autocomplete
                    className="player-compare-search"
                    PopperComponent={ComparePopper}
                    forcePopupIcon={false}
                    options={compareOptions}
                    value={comparePlayer}
                    inputValue={searchInput}
                    onInputChange={(event, value, reason) => {
                        if (reason === "reset") return;
                        handleSearchInputChange(value);
                    }}
                    onChange={(event, option) => {
                        if (option) {
                            setComparePlayer(option);
                            setSearchInput(`${option.first_name} ${option.last_name}`);
                        }
                    }}
                    getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                    isOptionEqualToValue={(option, value) => option.player_id === value?.player_id}
                    noOptionsText="Type at least 3 characters to search"
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Compare with another player"
                            size="small"
                            sx={compareFieldSx}
                        />
                    )}
                />
                {comparePlayer && comparePlayerData && (
                    <ToggleButtonGroup
                        className="player-compare-view-toggle"
                        value={viewMode}
                        exclusive
                        size="small"
                        onChange={(event, next) => {
                            if (next) setViewMode(next);
                        }}
                        aria-label="Comparison view"
                        sx={viewToggleSx}
                    >
                        <ToggleButton value="stats" aria-label="Stats view">
                            <TableChartOutlinedIcon />
                            Stats
                        </ToggleButton>
                        <ToggleButton value="graph" aria-label="Graph view" disabled={!hasGraphData}>
                            <ShowChartOutlinedIcon />
                            Graph
                        </ToggleButton>
                    </ToggleButtonGroup>
                )}
                {comparePlayer && (
                    <IconButton
                        className="player-compare-clear"
                        onClick={clearCompare}
                        aria-label="Clear comparison"
                        size="small"
                    >
                        <ClearRoundedIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>

            <Box
                className={`player-compare-scroll${viewMode === "graph" && comparePlayer ? " player-compare-scroll--graph" : ""}`}
            >
                {(loadingPrimary || loadingCompare) && (
                    <Box className="player-compare-loading">
                        <CircularProgress size={26} sx={{ color: "var(--brand-strong)" }} />
                    </Box>
                )}

                {!comparePlayer && !loadingCompare && (
                    <Box className="player-compare-empty">
                        <CompareArrowsRoundedIcon className="player-compare-empty-icon" aria-hidden />
                        <Typography className="player-compare-empty-title" variant="subtitle2">
                            Select a player to compare
                        </Typography>
                        <Typography className="player-compare-empty-caption" variant="body2">
                            Search for another prospect to compare stats and rankings with {primaryName}.
                        </Typography>
                    </Box>
                )}

                {comparePlayer && comparePlayerData && !loadingCompare && (
                    <>
                        {viewMode === "stats" && (
                            <CompareStatsPanel
                                playerData={playerData}
                                comparePlayerData={comparePlayerData}
                                primaryStats={primaryStats}
                                compareStats={compareStats}
                                primaryConsensus={primaryConsensus}
                                compareConsensus={compareConsensus}
                            />
                        )}

                        {viewMode === "graph" && (
                            <Box className="player-compare-graph-view">
                                {hasGraphData ? (
                                    <PlayerComparisonGraph
                                        graphDataOne={primaryGraphData}
                                        graphDataTwo={compareGraphData}
                                        playerOneData={playerData}
                                        playerTwoData={comparePlayerData}
                                        compact
                                    />
                                ) : (
                                    <Typography className="player-compare-graph-empty" variant="body2">
                                        No ranking history available for this comparison.
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
}

export default PlayerCompareTab;
